import { useRef, useState } from 'react'
import { Camera, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cropToSquare } from '@/lib/squareCrop'
import { describeUploadFailure, MAX_UPLOAD_MB, uploadBlob } from '@/lib/upload'
import { cn } from '@/lib/utils'

/**
 * Choosing a profile photo.
 *
 * Square, and cropped by default. An avatar is drawn in a circle everywhere it appears, so a
 * portrait that is not square is going to be cut somewhere no matter what — doing it here,
 * visibly, is the difference between "that is the picture I chose" and "the app cut my head
 * off". The crop is centred, which is right for the overwhelmingly common case.
 *
 * It also all but removes the size limit. What is uploaded is a 512px JPEG, so a 4MB photo
 * off a phone leaves here at about forty kilobytes and the ceiling is never reached in
 * practice. The disclaimer still names it, because the check happens on the file you PICK —
 * a browser has to decode an image before it can crop it, and decoding a 40-megapixel file
 * to throw most of it away is how a tab runs out of memory.
 */

/** What the surrounding form needs back: the storage key, or null for the default avatar. */
export type PhotoValue = string | null

const ACCEPT = 'image/png,image/jpeg,image/webp,image/heic,image/gif'

export function ProfilePhotoField({
  currentUrl,
  displayName,
  onSaved,
  disabled = false,
}: {
  /** What is on the account now — a signed URL, or null for the default. */
  currentUrl: string | null
  displayName: string
  /**
   * Called with the new key once the API has accepted it, or null once it is cleared.
   *
   * Returns what the API says the photo is now — a loadable URL, or null. That return value
   * is the only thing that can distinguish "saved" from "the server accepted the request and
   * still reports no photo", and those look identical on screen without it.
   */
  onSaved: (key: PhotoValue) => Promise<string | null | void> | string | null | void
  disabled?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /*
   * The photo saved, and then would not load.
   *
   * Worth its own state because an `<img>` that fails is SILENT — it falls back to the
   * default mark and looks identical to never having set one. That single ambiguity is what
   * makes this problem unreportable: "it didn't save" and "it saved but storage will not
   * serve it back" need completely different fixes, and until the app says which, nobody can
   * tell them apart, including whoever is asked to look into it.
   */
  const [unreadable, setUnreadable] = useState(false)
  /*
   * The request succeeded and the photo still is not there.
   *
   * The gap between "it threw" and "it will not load" — and the one state that produced no
   * message at all. Everything that goes wrong LOUDLY is caught below; this is what is left
   * when nothing goes wrong and nothing happens either, which is the hardest failure to
   * report because there is nothing on screen to describe.
   */
  const [savedButAbsent, setSavedButAbsent] = useState(false)
  /** What the API said, which beats the refetched identity: it is the response to THIS write. */
  const [justSaved, setJustSaved] = useState<string | null>(null)

  /*
   * The local preview is shown only WHILE the save is in flight.
   *
   * Keeping it afterwards felt better and lied: it is a data: URL made on this machine, so
   * it renders whatever the server did with the photo, and "it looked right in the dialog
   * and was gone on reload" is exactly the report that follows. Once the save has been
   * acknowledged and the identity refetched, what is on screen is what the API sends back —
   * so if the round trip is broken, it is visible here, at the moment somebody is looking to
   * see whether it worked.
   */
  const shown = busy ? (preview ?? currentUrl) : (justSaved ?? currentUrl)

  async function choose(file: File | undefined) {
    if (!file) return
    setError(null)

    /*
     * Checked before the crop, not after.
     *
     * The crop is what makes the output small, but getting there means decoding the original
     * at full resolution — so a file large enough to matter is a file large enough to hurt,
     * and the point of refusing is to refuse before that decode, not after it.
     */
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`That photo is larger than ${MAX_UPLOAD_MB}MB. Pick a smaller one.`)
      return
    }

    setBusy(true)
    setUnreadable(false)
    setSavedButAbsent(false)
    try {
      const cropped = await cropToSquare(file)
      setPreview(cropped.previewUrl)
      const key = await uploadBlob(cropped.blob, 'profile.jpg', 'image/jpeg')
      const stored = await onSaved(key)
      setPreview(null)
      /*
       * `undefined` means the caller does not report an outcome, which is not the same as
       * reporting that there is none — only an explicit null is evidence of a photo that
       * went missing between being accepted and being read back.
       */
      if (stored === null) setSavedButAbsent(true)
      else if (typeof stored === 'string') setJustSaved(stored)
    } catch (err) {
      setPreview(null)
      setError(describeUploadFailure(err))
    } finally {
      setBusy(false)
    }
  }

  async function clear() {
    setError(null)
    setBusy(true)
    setSavedButAbsent(false)
    try {
      setPreview(null)
      setJustSaved(null)
      await onSaved(null)
    } catch (err) {
      setError(describeUploadFailure(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-3">
      <div className="flex items-center gap-4">
        {/*
          A square frame, not a circle. The avatar is round everywhere it is USED, and showing
          the round result here would hide the corners that are about to be stored — the point
          of this preview is to show exactly what was kept.
        */}
        <div
          className={cn(
            'relative grid size-24 shrink-0 place-items-center overflow-hidden',
            'rounded-lg border border-border bg-muted',
          )}
        >
          {shown ? (
            <img
              src={shown}
              alt={displayName}
              className="size-full object-cover"
              referrerPolicy="no-referrer"
              onLoad={() => setUnreadable(false)}
              // A data: URL cannot fail; anything that does came from storage.
              onError={() => setUnreadable(!busy)}
            />
          ) : (
            <img
              src="/brand/Taptalent.ai%20Logo.png"
              alt="Default avatar"
              className="size-full object-cover"
            />
          )}
          {busy ? (
            <span className="absolute inset-0 grid place-items-center bg-card/70">
              <Loader2 className="size-5 animate-spin text-text-secondary" />
            </span>
          ) : null}
        </div>

        <div className="grid gap-2">
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              void choose(e.target.files?.[0])
              e.target.value = ''
            }}
          />
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              disabled={disabled || busy}
              onClick={() => input.current?.click()}
            >
              <Camera className="size-3.5" />
              {shown ? 'Change photo' : 'Upload photo'}
            </Button>
            {currentUrl ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-text-secondary"
                disabled={disabled || busy}
                onClick={() => void clear()}
              >
                <Trash2 className="size-3.5" />
                Remove
              </Button>
            ) : null}
          </div>
          <p className="text-tiny text-text-muted">
            Square, cropped from the middle. PNG or JPEG, up to {MAX_UPLOAD_MB}MB.
          </p>
        </div>
      </div>

      {error ? (
        <p className="rounded-md border border-danger/40 bg-danger/5 px-2 py-1.5 text-tiny text-danger">
          {error}
        </p>
      ) : savedButAbsent ? (
        <p className="rounded-md border border-danger/40 bg-danger/5 px-2 py-1.5 text-tiny text-danger">
          The upload finished and the change was accepted, but the server still reports no
          photo on your account — so nothing has visibly changed. This is a configuration
          fault, not something you did. Whoever administers this workspace should check that
          the API has an attachments bucket configured for this environment.
        </p>
      ) : unreadable ? (
        <p className="rounded-md border border-warning/40 bg-warning-soft px-2 py-1.5 text-tiny text-warning">
          Your photo is saved, but storage would not serve it back — so everyone still sees
          the default. Nothing is wrong with the picture you chose. Ask whoever administers
          this workspace to check that the API can read from the attachments bucket.
        </p>
      ) : null}
    </div>
  )
}
